/**
 * Manual purchase-order builder (roadmap Phase 3d). Lets an operator hand-compose a PO for blanks the
 * auto-draft never surfaces — proactively stocking a NEW style, or topping up something above par for an
 * upcoming job. Two canonical pick modes (never a raw SKU): "Tracked blank" filters the inventory overview
 * (so on-hand + on-order context shows), "New style" reuses the Add-stock Style→Color→Size flow to resolve
 * a canonical S&S SKU. Both feed a single review table, then the EXISTING `POST /purchase-orders` create
 * path with `source: 'manual'` (the server re-resolves cost, rejects any un-orderable SKU, and canonicalizes).
 *
 * Money-adjacent guardrails: (1) its OWN idempotency token, never shared with the draft page's create;
 * (2) a duplicate-open-PO warning — a line whose SKU already has open on-order must be explicitly confirmed
 * before Create, so an operator never silently doubles the on-order and suppresses a real reorder.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FilePlus2, Loader2, Plus, Trash2 } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { cn } from '@/lib/utils';
import { blankLabel, invalidateInventory, pickSizeRows, variantLabel } from './inventory-lib';
import { parseOrderQty } from './purchase-draft-lib';
import type { PurchaseOrderView } from './purchase-order-lib';
import { ColorCombobox } from './screen-print-quote/color-combobox';
import { StyleCombobox, type StylePick } from './screen-print-quote/style-combobox';

type OverviewRow = components['schemas']['InventoryOverviewRow'];

/** A line the operator has composed — identity + an editable quantity string (parsed at submit). */
type ManualLine = {
  ssSku: string;
  brandName?: string;
  styleName?: string;
  colorName?: string;
  sizeName?: string;
  qty: string;
};

/** Distinct-line cap: matches the server's priced/existence-verified ceiling (60), so every line is
 *  cost-resolved AND checked to exist — no unpriced/unverified tail. */
const MAX_LINES = 60;

export function ManualPoDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (po: PurchaseOrderView) => void;
}) {
  // This builder's OWN idempotency token — NEVER the draft page's. Held HERE, in the always-mounted
  // wrapper (not in ManualPoBody, which is unmounted on close via `{open && …}`), so it SURVIVES a
  // close→reopen: a create whose response was dropped can be retried after reopening and the server
  // replays the same PO instead of minting a second one that would double the on-order netting. Rotated
  // on success so the *next* PO is distinct.
  const tokenRef = useRef<string>(crypto.randomUUID());
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] sm:max-w-2xl">
        {open && <ManualPoBody onClose={onClose} onCreated={onCreated} tokenRef={tokenRef} />}
      </DialogContent>
    </Dialog>
  );
}

function ManualPoBody({
  onClose,
  onCreated,
  tokenRef,
}: {
  onClose: () => void;
  onCreated: (po: PurchaseOrderView) => void;
  tokenRef: { current: string };
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [mode, setMode] = useState<'tracked' | 'style'>('tracked');
  const [lines, setLines] = useState<ManualLine[]>([]);
  const [onOrderAcknowledged, setOnOrderAcknowledged] = useState(false);

  // On-order per SKU across all open POs — drives the duplicate-open-PO warning. `gcTime: 0` drops the
  // cache the moment the dialog closes, so a REOPEN always starts in `isPending` (never served stale
  // cached data while a background refetch is in flight). Combined with the fail-closed gate below, this
  // closes the reopen race where a stale map could read a since-ordered SKU as "0 on order".
  const onOrderQuery = useQuery({
    queryKey: ['purchase-orders', 'open-on-order'],
    gcTime: 0,
    queryFn: async () => {
      const { data, error } = await client.GET('/purchase-orders/open-on-order');
      if (error) throw new Error(error?.error?.detail ?? 'Could not load on-order data.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  const onOrderBySku = useMemo(
    () => new Map((onOrderQuery.data?.items ?? []).map((i) => [i.ssSku, i.onOrder])),
    [onOrderQuery.data],
  );
  const onOrderOf = (ssSku: string) => onOrderBySku.get(ssSku) ?? 0;

  /** Add a line (qty 1) or bump an existing one — dedupe by canonical SKU (the server rejects dup SKUs). */
  const addLine = (line: Omit<ManualLine, 'qty'>) => {
    setOnOrderAcknowledged(false); // a new line may itself be on-order — re-require the confirm
    setLines((prev) => {
      const existing = prev.find((l) => l.ssSku === line.ssSku);
      if (existing) {
        const bumped = (parseOrderQty(existing.qty) ?? 0) + 1;
        return prev.map((l) => (l.ssSku === line.ssSku ? { ...l, qty: String(bumped) } : l));
      }
      if (prev.length >= MAX_LINES) return prev; // capped — surfaced in the UI below
      return [...prev, { ...line, qty: '1' }];
    });
  };
  const setQty = (ssSku: string, qty: string) =>
    setLines((prev) => prev.map((l) => (l.ssSku === ssSku ? { ...l, qty } : l)));
  const removeLine = (ssSku: string) => setLines((prev) => prev.filter((l) => l.ssSku !== ssSku));

  const invalidCount = lines.filter((l) => {
    const q = parseOrderQty(l.qty);
    return q === null || q <= 0;
  }).length;
  const linesOnOrder = lines.filter((l) => onOrderOf(l.ssSku) > 0);
  const atCap = lines.length >= MAX_LINES;
  // The dup-open-PO guardrail must FAIL CLOSED: if the on-order map couldn't load, we can't tell whether a
  // line duplicates an open PO, so treat that as "must confirm" rather than silently skipping the check
  // (an empty map from a failed query would otherwise read as "nothing on order" and pass — the exact
  // over-net → hidden-reorder hole this feature exists to close). Pending is transient — just wait.
  const onOrderPending = onOrderQuery.isPending;
  const onOrderUnknown = onOrderQuery.isError;
  const needsAck = linesOnOrder.length > 0 || onOrderUnknown;
  // Create is blocked until: ≥1 line, all quantities valid, the on-order check has loaded, and — when a
  // line is (or may be) already on order — the operator has explicitly confirmed.
  const canSubmit =
    lines.length > 0 && invalidCount === 0 && !onOrderPending && (!needsAck || onOrderAcknowledged);

  const createPo = useMutation({
    mutationFn: async () => {
      const body = {
        lines: lines.map((l) => ({
          ssSku: l.ssSku,
          brandName: l.brandName,
          styleName: l.styleName,
          colorName: l.colorName,
          sizeName: l.sizeName,
          // canSubmit gates invalidCount === 0, so parseOrderQty is a positive int here.
          orderQty: parseOrderQty(l.qty) ?? 0,
        })),
        clientToken: tokenRef.current,
        source: 'manual' as const,
      };
      const { data, error } = await client.POST('/purchase-orders', { body });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to create the purchase order.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: (po) => {
      tokenRef.current = crypto.randomUUID(); // rotate so the next manual PO is distinct
      setLines([]);
      setOnOrderAcknowledged(false);
      invalidateInventory(qc); // busts the ['purchase-orders'] prefix → panel + open-on-order refetch
      onCreated(po);
      onClose();
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>Build a manual PO</DialogTitle>
        <DialogDescription>
          Order blanks the reorder draft doesn't list — restock a new style, or top up for a job.
          Pick by blank (never a SKU); the created PO joins the list below to Mark ordered.{' '}
          <span className="font-medium text-foreground">
            This does not place the order in S&amp;S.
          </span>
        </DialogDescription>
      </DialogHeader>

      {/* Pick-mode toggle */}
      <div className="flex items-center gap-1 rounded-md bg-muted p-0.5 text-xs font-medium">
        <button
          type="button"
          onClick={() => setMode('tracked')}
          className={cn(
            'flex-1 rounded px-2 py-1',
            mode === 'tracked' ? 'bg-background shadow-sm' : 'text-muted-foreground',
          )}
        >
          Tracked blank
        </button>
        <button
          type="button"
          onClick={() => setMode('style')}
          className={cn(
            'flex-1 rounded px-2 py-1',
            mode === 'style' ? 'bg-background shadow-sm' : 'text-muted-foreground',
          )}
        >
          New style
        </button>
      </div>

      {mode === 'tracked' ? (
        <TrackedPicker onAdd={addLine} onOrderOf={onOrderOf} disabled={atCap} />
      ) : (
        <StylePicker onAdd={addLine} onOrderOf={onOrderOf} disabled={atCap} />
      )}

      {atCap && (
        <p className="text-xs text-muted-foreground">
          A manual PO is capped at {MAX_LINES} lines. Create this one, then build another.
        </p>
      )}

      {/* Review table */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground">
          Lines to order {lines.length > 0 && `(${lines.length})`}
        </Label>
        {lines.length === 0 ? (
          <p className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
            No lines yet. Pick a blank above to add it.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-[480px] text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                    Blank
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                    On order
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                    Qty
                  </th>
                  <th className="w-10 px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const q = parseOrderQty(l.qty);
                  const invalid = q === null || q <= 0;
                  const onOrder = onOrderOf(l.ssSku);
                  const variant = variantLabel(l);
                  return (
                    <tr key={l.ssSku} className="border-t [&:nth-child(even)]:bg-muted/20">
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium">{blankLabel(l)}</div>
                        {variant && <div className="text-xs text-muted-foreground">{variant}</div>}
                        <div className="font-mono text-xs text-muted-foreground">{l.ssSku}</div>
                      </td>
                      <td className="px-3 py-2 text-right align-top">
                        {onOrder > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
                            <AlertTriangle className="size-3.5" />
                            {onOrder}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Input
                          type="number"
                          inputMode="numeric"
                          step="1"
                          min="1"
                          aria-label={`Order quantity for ${l.ssSku}`}
                          aria-invalid={invalid}
                          className={cn(
                            'h-8 w-20 text-right',
                            invalid &&
                              'border-destructive text-destructive focus-visible:ring-destructive',
                          )}
                          value={l.qty}
                          onChange={(e) => setQty(l.ssSku, e.target.value)}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          aria-label={`Remove ${l.ssSku}`}
                          onClick={() => removeLine(l.ssSku)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Duplicate-open-PO confirm — never silently double the on-order (a hidden reorder → stockout).
          Renders when a line is known-on-order OR the on-order check itself failed (fail closed: we can't
          rule out a duplicate, so require an explicit acknowledgment either way). */}
      {lines.length > 0 && needsAck && (
        <Alert variant="default" className="border-amber-500/50">
          <AlertTriangle className="size-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription>
            <label htmlFor="manual-po-on-order-confirm" className="flex items-start gap-2">
              <Checkbox
                id="manual-po-on-order-confirm"
                checked={onOrderAcknowledged}
                onCheckedChange={(v) => setOnOrderAcknowledged(v === true)}
                aria-label="Confirm ordering blanks already on order"
                className="mt-0.5"
              />
              <span className="text-sm">
                {onOrderUnknown ? (
                  <>
                    Couldn't check what's already on order. Confirm you want to create this PO
                    anyway — a blank already on an open PO would add to what's coming.
                  </>
                ) : (
                  <>
                    {linesOnOrder.length} blank{linesOnOrder.length === 1 ? ' is' : 's are'} already
                    on an open PO. Confirm you want to order{' '}
                    <span className="font-medium">more</span> — this adds to what's already coming.
                  </>
                )}
              </span>
            </label>
          </AlertDescription>
        </Alert>
      )}

      {onOrderPending && lines.length > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> Checking what's already on order…
        </p>
      )}

      {createPo.error instanceof Error && (
        <p className="text-sm text-destructive">{createPo.error.message}</p>
      )}
      {invalidCount > 0 && (
        <p className="text-xs text-destructive">
          Fix {invalidCount} invalid quantit{invalidCount === 1 ? 'y' : 'ies'} (whole number &gt;
          0).
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => createPo.mutate()}
          disabled={!canSubmit || createPo.isPending}
        >
          {createPo.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <FilePlus2 className="size-4" />
          )}
          {createPo.isPending ? 'Creating…' : 'Create PO'}
        </Button>
      </div>
    </>
  );
}

/** Mode A — search the inventory overview and add a tracked blank (on-hand + on-order shown for context). */
function TrackedPicker({
  onAdd,
  onOrderOf,
  disabled,
}: {
  onAdd: (line: Omit<ManualLine, 'qty'>) => void;
  onOrderOf: (ssSku: string) => number;
  disabled: boolean;
}) {
  const client = useApiClient();
  const [search, setSearch] = useState('');
  const overviewQuery = useQuery({
    queryKey: ['inventory-overview'],
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/overview');
      if (error) throw new Error(error?.error?.detail ?? 'Could not load inventory.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const rows: OverviewRow[] = overviewQuery.data?.rows ?? [];
  const q = search.trim().toLowerCase();
  const matches = (r: OverviewRow) =>
    q === '' || `${blankLabel(r)} ${variantLabel(r)} ${r.ssSku}`.toLowerCase().includes(q);
  const filtered = rows.filter(matches).slice(0, 40);

  return (
    <div className="space-y-2">
      <Input
        placeholder="Search tracked blanks (brand, style, color, size, SKU)…"
        aria-label="Search tracked blanks"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {overviewQuery.isPending ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading blanks…
        </div>
      ) : overviewQuery.isError ? (
        <p className="text-sm text-destructive">
          {overviewQuery.error instanceof Error ? overviewQuery.error.message : 'Failed to load.'}
        </p>
      ) : filtered.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">No matching tracked blanks.</p>
      ) : (
        <div className="max-h-56 overflow-y-auto rounded-md border">
          {filtered.map((r) => {
            const onOrder = onOrderOf(r.ssSku);
            return (
              <button
                key={r.ssSku}
                type="button"
                disabled={disabled}
                onClick={() => onAdd(r)}
                className="flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/50 disabled:opacity-50"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{blankLabel(r)}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {variantLabel(r)} · {r.ssSku}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
                  <span
                    className={cn(r.onHand < 0 && 'font-semibold text-red-700 dark:text-red-400')}
                  >
                    {r.onHand} on hand
                  </span>
                  {onOrder > 0 && (
                    <span className="text-amber-700 dark:text-amber-400">{onOrder} on order</span>
                  )}
                  <Plus className="size-4" />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Mode B — Style → Color → per-size grid (reuses the Add-stock flow); each size resolves a canonical SKU. */
function StylePicker({
  onAdd,
  onOrderOf,
  disabled,
}: {
  onAdd: (line: Omit<ManualLine, 'qty'>) => void;
  onOrderOf: (ssSku: string) => number;
  disabled: boolean;
}) {
  const client = useApiClient();
  const [stylePick, setStylePick] = useState<StylePick | null>(null);
  const [color, setColor] = useState('');
  const style = stylePick?.style ?? '';

  const variantsQuery = useQuery({
    queryKey: ['style-variants', style],
    enabled: style.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/garments/by-style/{style}/variants', {
        params: { path: { style } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Could not load sizes.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const sizeRows = pickSizeRows(variantsQuery.data?.variants ?? [], color);
  const brandName = variantsQuery.data?.brand ?? stylePick?.brand;
  const styleName = variantsQuery.data?.styleName ?? stylePick?.styleName;

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label className="text-xs font-medium text-muted-foreground">Blank</Label>
        <StyleCombobox
          value={style}
          onPick={(pick) => {
            setStylePick(pick);
            setColor('');
          }}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs font-medium text-muted-foreground">Color</Label>
        <ColorCombobox style={style} value={color} onPick={setColor} />
      </div>

      {color &&
        (variantsQuery.isPending ? (
          <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading sizes…
          </div>
        ) : variantsQuery.isError ? (
          <p className="text-sm text-destructive">
            {variantsQuery.error instanceof Error ? variantsQuery.error.message : 'Failed to load.'}
          </p>
        ) : sizeRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No sizes for this color.</p>
        ) : (
          <div className="max-h-56 overflow-y-auto rounded-md border">
            {sizeRows.map((v) => {
              const onOrder = onOrderOf(v.sku);
              return (
                <button
                  key={v.sku}
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    onAdd({
                      ssSku: v.sku,
                      brandName,
                      styleName,
                      colorName: v.colorName,
                      sizeName: v.sizeName,
                    })
                  }
                  className="flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/50 disabled:opacity-50"
                >
                  <span className="font-medium">{v.sizeName}</span>
                  <span className="flex items-center gap-3 text-xs text-muted-foreground">
                    {onOrder > 0 && (
                      <span className="text-amber-700 dark:text-amber-400">{onOrder} on order</span>
                    )}
                    <Plus className="size-4" />
                  </span>
                </button>
              );
            })}
          </div>
        ))}
    </div>
  );
}
